// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { IPolicyExtensions, IPolicyGroup, ProtectionLevel } from 'src/types/d3ePolicy';
import { ProtectionLevelBottomSheetComponent } from './protection-level-bottom-sheet/protetction-level-bottom-sheet.component';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-default-file-format',
    templateUrl: './default-file-format.component.html',
    styleUrls: ['./default-file-format.component.scss'],
    standalone: false
})
export class DefaultFileFormatComponent implements OnChanges {
  @Input() fileFormats: IPolicyGroup[];
  @Input() defaultFileFormats: IPolicyGroup[];
  @Input() policyExtensions: IPolicyExtensions = { protectedExtensions: [], lockedExtensions: [] };
  @Input() showEnabledButton: boolean;
  @Input() policyEnabled: boolean;
  @Input() allowAlwaysOn: boolean;
  @Input() import: IPolicyExtensions = {disabledExtensions: [], protectedExtensions: [], lockedExtensions: []}
  @Output() policyExtensionsChanged: EventEmitter<IPolicyExtensions> = new EventEmitter<IPolicyExtensions>();
  @Output() policyEnableToggleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() allowAlwaysOnChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  _policyExtensions: IPolicyExtensions;
  toggleGroupAppearance: 'legacy' | 'standard' = 'standard';  // make input
  fAlwaysOnTitle: string;
  fAlwaysOnContent: string;
  fYes: string;
  fNo: string;
  fTopicDescription: string

  selectLevelList: string[] = ['All'];
  selectedLevel = 'All';

  constructor(private bottomSheet: MatBottomSheet,
              private dialog: MatDialog,
              private translate: TranslateService
  ) {
    this.translate.get(['ALLOW_ALWAYS_ON_TITLE', 'ALLOW_ALWAYS_ON_CONTENT', 'YES', 'NO',
      'TOPIC_DESCRIPTION']).subscribe(vals => {
        this.fAlwaysOnTitle = vals['ALLOW_ALWAYS_ON_TITLE'];
        this.fAlwaysOnContent = vals['ALLOW_ALWAYS_ON_CONTENT'];
        this.fYes = vals['YES'];
        this.fNo = vals['NO'];
        this.fTopicDescription = vals['TOPIC_DESCRIPTION'];
    });
    this.showEnabledButton = false;
    this.policyEnabled = true;
    this.allowAlwaysOn = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.policyExtensions && changes.policyExtensions.currentValue) {
      this.updatePolicyExtensions(changes.policyExtensions.currentValue);
    }

    if (changes.allowAlwaysOn) {
      this.allowAlwaysOn = !!changes.allowAlwaysOn.currentValue;
    }

    if (changes.defaultFileFormats && changes.defaultFileFormats.firstChange) {
      const names = changes.defaultFileFormats.currentValue.map(v => v.name);
      this.selectLevelList = [...this.selectLevelList,  ...names];
    }

    if (changes.import && changes.import.previousValue !== changes.import.currentValue) {
      this.import = changes.import.currentValue;

      this.import.disabledExtensions.forEach(i => {
        this.onUpdateImportExtension(i, 'none');
      });

      this.import.protectedExtensions.forEach(i => {
        this.onUpdateImportExtension(i, 'medium');
      });

      this.import.lockedExtensions.forEach(i => {
        this.onUpdateImportExtension(i, 'high');
      });
    }
    
    if (this.showEnabledButton) {
      if (changes.policyEnabled && changes.policyEnabled.previousValue !== changes.policyEnabled.currentValue) {
        this.policyEnabled = changes.policyEnabled.currentValue;
      }
    } else {
      this.policyEnabled = true;
    }
  }

  updatePolicyExtensions(policyExtensions: IPolicyExtensions) {
    this._policyExtensions = JSON.parse(JSON.stringify(policyExtensions));
  }

  getToggleTooltip() {
    return this.policyEnabled ? 'Click to disable the policy.' : 'Click to enable the policy.';
  }

  onToggleEnableChange(event: MatSlideToggleChange) {
    this.policyEnabled = event.checked;
    this.policyEnableToggleChange.emit(event.checked);
  }

  // This forces the accessibility narrator to read the extensions as individual letters 
  // and not try to pronounce the extension as a word.
  getAriaLabel(extension: string) {
    return extension.split('').join(' ');
  }

  onToggleSettingChanged(event) {
    const extensionSettings: string[] = event.value.split(':');
    const nameIndex = this.fileFormats.findIndex(f => {
      // A fileFormat name may have a space in the name (Photoshop Elements), so replace
      // the space with a dash to match the extensionSettings array.
      return f.name.toLowerCase().trim().replace(' ', '-') === extensionSettings[0].toLowerCase();
    });
    const extSettingIndex = this.fileFormats[nameIndex].extensions.findIndex(e =>
      e.extension.toLowerCase() === extensionSettings[1].toLowerCase()
    );
    const extension = this.fileFormats[nameIndex].extensions[extSettingIndex].extension;

    // Make sure the extension is removed from all three groups.
    // This acts like a dedupe in case the extension exists already in the same group we are about to add it to.
    this._policyExtensions.disabledExtensions = this._policyExtensions.disabledExtensions.filter(e => e !== extension);
    this._policyExtensions.protectedExtensions = this._policyExtensions.protectedExtensions.filter(e => e !== extension);
    this._policyExtensions.lockedExtensions = this._policyExtensions.lockedExtensions.filter(e => e !== extension);

    // Add the extension to the appropriate extension group (disabled, protected, locked)
    // and set the fileFormat (none, medium, high) to the new value.
    const extensionValue = extensionSettings[2].toLowerCase();
    switch (extensionValue) {
      case 'medium':
        this._policyExtensions.protectedExtensions.push(extension);
        this.fileFormats[nameIndex].extensions[extSettingIndex].value = extensionValue;
        break;
      case 'high':
        this._policyExtensions.lockedExtensions.push(extension);
        this.fileFormats[nameIndex].extensions[extSettingIndex].value = extensionValue;
        break;
      default:
        // 'none' case
        this._policyExtensions.disabledExtensions.push(extension);
        this.fileFormats[nameIndex].extensions[extSettingIndex].value = 'none';
        break;
    }

    this.policyExtensionsChanged.emit(this._policyExtensions);
  }

  onProtectionLevelClick() {
    if (this.policyEnabled) {
      this.bottomSheet.open(ProtectionLevelBottomSheetComponent, {
        data: {
          topicDescription: this.fTopicDescription
        },
        disableClose: false,
        closeOnNavigation: true,
        autoFocus: true,
        hasBackdrop: true,
      });
    }
  }

  onSetRecommendedClick() {
    if (this.allowAlwaysOn) {
      this.setRecommended();
    } else {
      const title = this.fAlwaysOnTitle;
      const content = this.fAlwaysOnContent;

      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title,
          content,
          rejectBtnName: this.fNo,
          acceptBtnName: this.fYes,
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.allowAlwaysOn = true;
          this.allowAlwaysOnChanged.emit(true);
          this.setRecommended();
        }
      });
    }
  }

  private setRecommended() {
      this.defaultFileFormats.forEach(f => {
        if (f.extensions) {
          f.extensions.forEach(e => {
            // Remove the extension to be updated from disabled, protected, and locked.
            // Removing from all acts like a dedupe in case the extension some how was added in different arrays
            // or multiple times in the same array (that should never happen though).
            this._policyExtensions.disabledExtensions = this._policyExtensions.disabledExtensions.filter(r =>
              r !== e.extension
            );
            this._policyExtensions.protectedExtensions = this._policyExtensions.protectedExtensions.filter(r =>
              r !== e.extension
            );
            this._policyExtensions.lockedExtensions = this._policyExtensions.lockedExtensions.filter(r =>
              r !== e.extension
            );

            // Set the fileFormat (none, medium, high) to the new value.
            this.fileFormats.find(
              ff => ff.name === f.name
              ).extensions.find(
                fe => fe.extension === e.extension
            ).value = e.value || 'none';

            // Add the extension to the appropriate extension group (disabled, protected, locked)
            const extensionValue = e.value.toLowerCase();
            switch (extensionValue) {
              case 'medium':
                this._policyExtensions.protectedExtensions.push(e.extension);
                break;
              case 'high':
                this._policyExtensions.lockedExtensions.push(e.extension);
                break;
              default:
                // 'none' case
                this._policyExtensions.disabledExtensions.push(e.extension);
                break;
            }

          });
        }
      });

      this.policyExtensionsChanged.emit(this._policyExtensions);
  }

  public setProtectionLevel(level: ProtectionLevel) {
    if (!(level === 'high' && !this.allowAlwaysOn)) {
      this.setAllToProtectionLevel(level);
    } else {
      const title = this.fAlwaysOnTitle;
      const content = this.fAlwaysOnContent;

      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title,
          content,
          rejectBtnName: this.fNo,
          acceptBtnName: this.fYes,
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.allowAlwaysOn = true;
          this.allowAlwaysOnChanged.emit(true);
          this.setAllToProtectionLevel(level);
        }
      });
    }
  }

  private setAllToProtectionLevel(level: ProtectionLevel) {
     this.fileFormats.forEach(f => {
        if (this.selectedLevel === 'All' || this.selectedLevel === f.name) {
          if (f.extensions) {
            f.extensions.forEach(e => {

            // Make sure the extension is removed from all three groups.
            // This acts like a dedupe in case the extension exists already in the same group we are about to add it to.
            this._policyExtensions.disabledExtensions = this._policyExtensions.disabledExtensions.filter(de => de !== e.extension);
            this._policyExtensions.protectedExtensions = this._policyExtensions.protectedExtensions.filter(pe => pe !== e.extension);
            this._policyExtensions.lockedExtensions = this._policyExtensions.lockedExtensions.filter(le => le !== e.extension);

            e.value = level;
              // Add the extension to the appropriate extension group (disabled, protected, locked)
              const extensionValue = e.value.toLowerCase();
              switch (extensionValue) {
                case 'medium':
                  this._policyExtensions.protectedExtensions.push(e.extension);
                  break;
                case 'high':
                  this._policyExtensions.lockedExtensions.push(e.extension);
                  break;
                default:
                  // 'none' case
                  this._policyExtensions.disabledExtensions.push(e.extension);
                  break;
              }

            });
          }
        }
      });

      this.policyExtensionsChanged.emit(this._policyExtensions);
  }

  public getImportProtectionLevel(setting: string) {
    if (setting === 'none') {
      return `none`;
    }
    if (setting === 'medium') {
      return `medium`;
    }
    if (setting === 'high') {
      return `high`;
    }
    return `medium`;
  }

  public onUpdateImportExtension(value: string, setting: string) {
    // Remove the extension to be updated from disabled, protected, and locked.
    // Removing from all acts like a dedupe in case the extension some how was added in different arrays
    // or multiple times in the same array (that should never happen though).          
    this._policyExtensions.disabledExtensions = this._policyExtensions.disabledExtensions.filter(r => r !== value);
    this._policyExtensions.protectedExtensions = this._policyExtensions.protectedExtensions.filter(r => r !== value);
    this._policyExtensions.lockedExtensions = this._policyExtensions.lockedExtensions.filter(r => r !== value);

    // Set the fileFormat (none, medium, high) to the new value.
    this.fileFormats.forEach(ff => {
      const currentFileFormat = ff.extensions.find(fe => fe.extension === value);
      if (currentFileFormat) {
        currentFileFormat.value = this.getImportProtectionLevel(setting);
      }
    });
    
    // Add the extension to the appropriate extension group (disabled, protected, locked)
    switch (value) {
      case 'medium':
        this._policyExtensions.protectedExtensions.push(value);
        break;
      case 'high':
        this._policyExtensions.lockedExtensions.push(value);
        break;
      default:
        // 'none' case
        this._policyExtensions.disabledExtensions.push(value);
        break;
    }
        
    this.policyExtensionsChanged.emit(this._policyExtensions);
  }

}
